import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CollectionSegment } from '../../../../../models/collection-segment';
import { Switch } from '../../../../../components/ui/switch';
import axios from 'axios';

export const Route = createFileRoute('/_app/$module_id/$entity_type_id/segments/manage')({
    component: RouteComponent,
});

interface UserSegmentViewModel {
    collectionSegment: CollectionSegment;
    visible: boolean;
}

function RouteComponent() {
    const { entity_type_id } = Route.useParams();
    const queryClient = useQueryClient();

    // Fetch segments for this collection
    const { data: segments, isLoading } = useQuery({
        queryKey: ['collection-segments', entity_type_id],
        queryFn: async () => {
            const response = await axios.get<UserSegmentViewModel[]>(
                `/CollectionSegments/collections/${entity_type_id}`
            );
            return response.data;
        },
        enabled: !!entity_type_id,
    });

    // Mutation to show a segment
    const showSegmentMutation = useMutation({
        mutationFn: async (segmentId: string) => {
            await axios.put(`/CollectionSegments/${segmentId}/show-segment`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collection-segments', entity_type_id] });
        },
    });

    // Mutation to hide a segment
    const hideSegmentMutation = useMutation({
        mutationFn: async (segmentId: string) => {
            await axios.put(`/CollectionSegments/${segmentId}/hide-segment`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collection-segments', entity_type_id] });
        },
    });

    const handleVisibilityChange = (segmentId: string, isVisible: boolean) => {
        if (isVisible) {
            showSegmentMutation.mutate(segmentId);
        } else {
            hideSegmentMutation.mutate(segmentId);
        }
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <h1 className="text-2xl font-bold text-gray-900">Manage Segments</h1>
                <div className="mt-4">Loading segments...</div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900">Manage Segments</h1>
            <p className="text-gray-600 mt-1 mb-6">
                Control which segments are visible in the segments list. Hidden segments can still be accessed directly via URL.
            </p>

            {!segments || segments.length === 0 ? (
                <div className="text-gray-500">No segments found for this entity type.</div>
            ) : (
                <div className="space-y-4">
                    {segments.map((userSegment: UserSegmentViewModel) => (
                        <div
                            key={userSegment.collectionSegment.id}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                        >
                            <div className="flex-1">
                                <h3 className="font-medium text-gray-900">{userSegment.collectionSegment.name}</h3>
                                {userSegment.collectionSegment.private && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mt-1">
                                        Private
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center space-x-3">
                                <span className="text-sm text-gray-600">
                                    {userSegment.visible ? 'Visible' : 'Hidden'}
                                </span>
                                <Switch
                                    checked={userSegment.visible}
                                    onCheckedChange={(checked) =>
                                        handleVisibilityChange(userSegment.collectionSegment.id!, checked)
                                    }
                                    disabled={showSegmentMutation.isPending || hideSegmentMutation.isPending}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">About Segment Visibility</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Hidden segments won't appear in the segments dropdown</li>
                    <li>• You can still access hidden segments directly via their URL</li>
                    <li>• The "All" segment is always visible and cannot be hidden</li>
                    <li>• Your visibility preferences are saved to your user profile</li>
                </ul>
            </div>
        </div>
    );
}
